import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import axios from "axios";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const ALL_STATUSES    = ["New", "Open", "On Hold", "Complete", "Abandoned", "Closed"];
const MANUAL_STATUSES = ["Open", "On Hold", "Complete", "Abandoned"];

const STATUS_META = {
  "New":       { color: "#92400E", bg: "#FEF3C7", dot: "#F59E0B" },
  "Open":      { color: "#991B1B", bg: "#FEE2E2", dot: "#EF4444" },
  "On Hold":   { color: "#0F172A", bg: "#F3F4F6", dot: "#374151" },
  "Complete":  { color: "#065F46", bg: "#D1FAE5", dot: "#34D399" },
  "Abandoned": { color: "#4B5563", bg: "#F3F4F6", dot: "#9CA3AF" },
  "Closed":    { color: "#6B7280", bg: "#F9FAFB", dot: "#D1D5DB" },
};

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const PRIORITY_META = {
  "Low":      { color: "#6B7280", icon: "↓" },
  "Medium":   { color: "#D97706", icon: "→" },
  "High":     { color: "#EA580C", icon: "↑" },
  "Critical": { color: "#DC2626", icon: "⚡" },
};

const PALETTE = [
  "#EF4444","#F97316","#F59E0B","#84CC16","#22C55E",
  "#06B6D4","#3B82F6","#8B5CF6","#EC4899","#6B7280",
];

const API = process.env.REACT_APP_API_URL;

// ─────────────────────────────────────────────────────────────────────────────
// API helper — attaches Cognito JWT to every request
// ─────────────────────────────────────────────────────────────────────────────
async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return { Authorization: `Bearer ${token}` };
}

async function apiGet(path) {
  const headers = await authHeaders();
  const res = await axios.get(`${API}${path}`, { headers });
  return res.data;
}

async function apiPost(path, data) {
  const headers = await authHeaders();
  const res = await axios.post(`${API}${path}`, data, { headers });
  return res.data;
}

async function apiPut(path, data) {
  const headers = await authHeaders();
  const res = await axios.put(`${API}${path}`, data, { headers });
  return res.data;
}

async function apiDelete(path) {
  const headers = await authHeaders();
  const res = await axios.delete(`${API}${path}`, { headers });
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// Normalizes any date value to YYYY-MM-DD — handles both "2026-05-30" and "2026-05-30T00:00:00.000Z"
function normDate(dateStr) {
  if (!dateStr) return "";
  return String(dateStr).slice(0, 10);
}

function fmtDateShort(dateStr) {
  const d = normDate(dateStr);
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function isOverdue(due, status) {
  if (!due || ["Complete", "Closed", "Abandoned"].includes(status)) return false;
  const [y, m, day] = normDate(due).split("-").map(Number);
  return new Date(y, m - 1, day) < new Date(new Date().toDateString());
}

function today() { return new Date().toISOString().split("T")[0]; }

function getCatColor(categories, categoryId) {
  return categories.find(c => c.id === categoryId)?.color || "#6B7280";
}

function getCatName(categories, categoryId) {
  return categories.find(c => c.id === categoryId)?.name || "Uncategorized";
}

// ─────────────────────────────────────────────────────────────────────────────
// UI Atoms
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META["New"];
  return (
    <span style={{ background: m.bg, color: m.color, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: 0.3, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: m.dot, flexShrink: 0 }} />
      {status}
    </span>
  );
}

function PriorityChip({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META["Medium"];
  return <span style={{ color: m.color, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>{m.icon} {priority}</span>;
}

function CategoryTag({ name, color }) {
  return (
    <span style={{ borderLeft: `3px solid ${color || "#6B7280"}`, paddingLeft: 6, color: color || "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", whiteSpace: "nowrap" }}>
      {name}
    </span>
  );
}

function Divider() {
  return <div style={{ borderBottom: "1px solid #F3F4F6", margin: "4px 0" }} />;
}

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
      <div style={{ width: 28, height: 28, border: "3px solid #E5E7EB", borderTopColor: "#2563EB", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Filter bar primitives
// ─────────────────────────────────────────────────────────────────────────────
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const allSelected  = options.length > 0 && selected.length === options.length;
  const noneSelected = selected.length === 0;
  const partial      = !allSelected && !noneSelected;

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          border: `1px solid ${partial ? "#93C5FD" : "#E5E7EB"}`,
          background: partial ? "#EFF6FF" : "#fff",
          color: partial ? "#2563EB" : "#374151",
          borderRadius: 8, padding: "7px 11px",
          cursor: "pointer", fontSize: 12, fontWeight: 600,
          fontFamily: "inherit", whiteSpace: "nowrap",
        }}
      >
        {label}
        {partial && (
          <span style={{
            background: "#2563EB", color: "#fff", borderRadius: 9,
            padding: "1px 7px", fontSize: 10, fontWeight: 700, lineHeight: 1.4,
          }}>{selected.length}</span>
        )}
        {noneSelected && <span style={{ color: "#9CA3AF", fontSize: 11 }}>· none</span>}
        <span style={{ fontSize: 10, color: "#9CA3AF" }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: 200, maxWidth: 260, maxHeight: 320, overflowY: "auto",
          background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10,
          boxShadow: "0 8px 24px rgba(15,42,110,.12)", zIndex: 40,
        }}>
          <div style={{ display: "flex", gap: 6, padding: "8px 10px", borderBottom: "1px solid #F3F4F6" }}>
            <button onClick={() => onChange(options.map(o => o.value))} style={chipBtn(allSelected)}>All</button>
            <button onClick={() => onChange([])} style={chipBtn(noneSelected)}>None</button>
          </div>
          <div style={{ padding: "4px 0" }}>
            {options.map(opt => {
              const isSelected = selected.includes(opt.value);
              return (
                <button key={opt.value} onClick={() => toggle(opt.value)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 9,
                    background: "none", border: "none", padding: "7px 12px",
                    cursor: "pointer", fontFamily: "inherit", fontSize: 13,
                    color: "#0F172A", textAlign: "left",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                    border: `1.5px solid ${isSelected ? "#2563EB" : "#D1D5DB"}`,
                    background: isSelected ? "#2563EB" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isSelected && (
                      <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {opt.color && (
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: opt.color, flexShrink: 0 }} />
                  )}
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const chipBtn = (active) => ({
  flex: 1, background: active ? "#EFF6FF" : "#F8FAFC",
  border: `1px solid ${active ? "#93C5FD" : "#E5E7EB"}`,
  color: active ? "#2563EB" : "#6B7280",
  borderRadius: 6, padding: "4px 0", fontSize: 11, fontWeight: 700,
  cursor: "pointer", fontFamily: "inherit",
});

function SortToggle({ label, isActive, direction, onClick }) {
  return (
    <button onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        border: `1px solid ${isActive ? "#93C5FD" : "#E5E7EB"}`,
        background: isActive ? "#EFF6FF" : "#fff",
        color: isActive ? "#2563EB" : "#6B7280",
        borderRadius: 8, padding: "7px 11px",
        cursor: "pointer", fontSize: 12, fontWeight: 600,
        fontFamily: "inherit", whiteSpace: "nowrap",
      }}
    >
      {label}
      <span style={{
        fontSize: 14, fontWeight: 800, lineHeight: 1,
        color: isActive ? "#2563EB" : "#D1D5DB",
        transition: "color .15s, transform .15s",
        transform: isActive && direction === "asc" ? "rotate(180deg)" : "none",
      }}>↓</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings Panel
// ─────────────────────────────────────────────────────────────────────────────
function SettingsPanel({ categories, onClose, onCategoriesChanged }) {
  const [cats, setCats]       = useState(categories.map(c => ({ ...c })));
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PALETTE[0]);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const addCat = () => {
    const name = newName.trim();
    if (!name || cats.find(c => c.name.toLowerCase() === name.toLowerCase())) return;
    setCats(cs => [...cs, { id: `temp-${Date.now()}`, name, color: newColor, locked: false, isNew: true }]);
    setNewName(""); setNewColor(PALETTE[0]);
  };

  const delCat    = (id) => setCats(cs => cs.filter(c => c.id !== id));
  const updateCat = (id, patch) => setCats(cs => cs.map(c => c.id === id ? { ...c, ...patch } : c));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const original = categories.map(c => c.id);
      const current  = cats.map(c => c.id);

      // Delete removed categories
      for (const id of original) {
        if (!current.includes(id)) {
          const cat = categories.find(c => c.id === id);
          if (!cat?.locked) await apiDelete(`/categories/${id}`);
        }
      }

      // Create new categories
      for (const cat of cats) {
        if (cat.isNew) {
          await apiPost("/categories", { name: cat.name, color: cat.color });
        }
      }

      // Update changed categories
      for (const cat of cats) {
        if (!cat.isNew && !cat.locked) {
          const orig = categories.find(c => c.id === cat.id);
          if (orig && (orig.name !== cat.name || orig.color !== cat.color)) {
            await apiPut(`/categories/${cat.id}`, { name: cat.name, color: cat.color });
          }
        }
      }

      await onCategoriesChanged();
      onClose();
    } catch (err) {
      setError("Failed to save categories. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const inputS = { border: "1px solid #D1D5DB", borderRadius: 7, padding: "7px 10px", fontSize: 13, color: "#0F172A", outline: "none", fontFamily: "inherit", background: "#fff" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,.25)" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "18px 20px 14px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 800, fontSize: 16, color: "#0F172A" }}>⚙ Manage Categories</div>
          <button onClick={onClose} style={{ background: "#F3F4F6", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 18, color: "#6B7280" }}>×</button>
        </div>

        {error && <div style={{ margin: "12px 20px 0", padding: "8px 12px", background: "#FEE2E2", color: "#B91C1C", borderRadius: 8, fontSize: 12 }}>{error}</div>}

        <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          {cats.map(cat => {
            const locked = !!cat.locked;
            return (
              <div key={cat.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: locked ? "#F3F4F6" : "#F9FAFB", borderRadius: 8, opacity: locked ? 0.75 : 1 }}>
                <input type="color" value={cat.color} disabled={locked}
                  onChange={e => updateCat(cat.id, { color: e.target.value })}
                  style={{ width: 28, height: 28, border: "none", borderRadius: 6, cursor: locked ? "not-allowed" : "pointer", padding: 2, background: "transparent" }} />
                {editId === cat.id ? (
                  <input style={{ ...inputS, flex: 1 }} value={cat.name}
                    onChange={e => updateCat(cat.id, { name: e.target.value })}
                    onBlur={() => setEditId(null)}
                    onKeyDown={e => e.key === "Enter" && setEditId(null)}
                    autoFocus />
                ) : (
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: "#374151" }}>{cat.name}</span>
                )}
                {locked ? (
                  <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 600, letterSpacing: 0.4, paddingRight: 4 }}>DEFAULT</span>
                ) : (
                  <>
                    <button onClick={() => setEditId(cat.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#9CA3AF", padding: 4 }} title="Rename">✎</button>
                    <button onClick={() => delCat(cat.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, color: "#F87171", padding: 4 }} title="Delete">✕</button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <Divider />

        <div style={{ padding: "12px 20px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
            style={{ width: 32, height: 32, border: "none", borderRadius: 6, cursor: "pointer", padding: 2, background: "transparent" }} />
          <input style={{ ...inputS, flex: 1, minWidth: 120 }} placeholder="New category name…" value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && addCat()} />
          <button onClick={addCat} style={{ background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, padding: "7px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Add</button>
        </div>
        <div style={{ padding: "0 20px 16px", display: "flex", gap: 6, flexWrap: "wrap" }}>
          {PALETTE.map(c => (
            <button key={c} onClick={() => setNewColor(c)}
              style={{ width: 22, height: 22, borderRadius: "50%", background: c, border: newColor === c ? "2px solid #0F2A6E" : "2px solid transparent", cursor: "pointer", padding: 0 }} />
          ))}
        </div>

        <Divider />
        <div style={{ padding: "12px 20px 18px", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ticket Drawer
// ─────────────────────────────────────────────────────────────────────────────
function TicketDrawer({ ticket, categories, onClose, onSaved, onDeleted }) {
  const isNew    = !ticket.id;
  const isClosed = ticket.status === "Closed";

  const [form, setForm]             = useState({ ...ticket });
  const [detail, setDetail]         = useState(null); // full ticket with history + comments
  const [commentText, setCommentText] = useState("");
  const [tab, setTab]               = useState(isNew ? "edit" : "detail");
  const [saving, setSaving]         = useState(false);
  const [loading, setLoading]       = useState(!isNew);
  const [error, setError]           = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Load full ticket detail (history + comments) when opening an existing ticket
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    apiGet(`/tickets/${ticket.id}`)
      .then(data => { setDetail(data); setForm({ ...data, due_date: normDate(data.due_date) }); })
      .catch(() => setError("Failed to load ticket details."))
      .finally(() => setLoading(false));
  }, [ticket.id, isNew]);

  const handleStatusChange = async (newStatus) => {
    try {
      const updated = await apiPut(`/tickets/${form.id}`, { ...form, status: newStatus });
      // Reload to get fresh history
      const fresh = await apiGet(`/tickets/${form.id}`);
      setDetail(fresh);
      setForm({ ...fresh, due_date: normDate(fresh.due_date) });
      onSaved(updated);
    } catch {
      setError("Failed to update status.");
    }
  };

  const addComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    try {
      await apiPost(`/tickets/${form.id}/comments`, { body: text });
      const fresh = await apiGet(`/tickets/${form.id}`);
      setDetail(fresh);
      setForm({ ...fresh, due_date: normDate(fresh.due_date) });
      setCommentText("");
    } catch {
      setError("Failed to post comment.");
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await apiDelete(`/tickets/${form.id}/comments/${commentId}`);
      const fresh = await apiGet(`/tickets/${form.id}`);
      setDetail(fresh);
      setForm({ ...fresh, due_date: normDate(fresh.due_date) });
    } catch {
      setError("Failed to delete comment.");
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const created = await apiPost("/tickets", {
          title:       form.title,
          body:        form.body,
          priority:    form.priority,
          category_id: form.category_id,
          due_date:    form.due_date || null,
        });
        onSaved(created);
      } else {
        const updated = await apiPut(`/tickets/${form.id}`, {
          title:       form.title,
          body:        form.body,
          status:      form.status,
          priority:    form.priority,
          category_id: form.category_id,
          due_date:    form.due_date || null,
        });
        onSaved(updated);
      }
      onClose();
    } catch {
      setError("Failed to save ticket.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await apiDelete(`/tickets/${ticket.id}`);
      onDeleted(ticket.id);
      onClose();
    } catch {
      setError("Failed to delete ticket.");
    }
  };

  const catColor = getCatColor(categories, form.category_id);
  const catName  = getCatName(categories, form.category_id);

  const inputS  = { width: "100%", border: "1px solid #D1D5DB", borderRadius: 7, padding: "8px 10px", fontSize: 13, color: "#0F172A", outline: "none", boxSizing: "border-box", fontFamily: "inherit" };
  const labelS  = { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block", letterSpacing: 0.3 };
  const selectS = { ...inputS, background: "#fff", cursor: "pointer" };

  // Unified timeline from history + comments
  const timeline = useMemo(() => {
    if (!detail) return [];
    const items = [
      ...(detail.history || []).map(h => ({ ...h, _kind: "history" })),
      ...(detail.comments || []).map(c => ({ ...c, _kind: "comment" })),
    ];
    return items.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [detail]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex" }} onClick={onClose}>
      <div style={{ flex: 1, background: "rgba(0,0,0,.35)" }} />
      <div style={{ width: "100%", maxWidth: 540, background: "#fff", height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", boxShadow: "-8px 0 40px rgba(0,0,0,.15)" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "16px 20px 0", borderBottom: "1px solid #F3F4F6", position: "sticky", top: 0, background: "#fff", zIndex: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, paddingBottom: 12 }}>
            <div style={{ minWidth: 0 }}>
              {!isNew && <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace", fontWeight: 600, marginBottom: 3 }}>TKT-{String(ticket.ticket_number).padStart(3, "0")}</div>}
              <div style={{ fontWeight: 800, fontSize: 15, color: "#0F172A", lineHeight: 1.3 }}>{isNew ? "New Ticket" : form.title}</div>
              {isClosed && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>This ticket is closed and read-only.</div>}
            </div>
            <button onClick={onClose} style={{ background: "#F3F4F6", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 18, color: "#6B7280", flexShrink: 0 }}>×</button>
          </div>
          {!isNew && (
            <div style={{ display: "flex", gap: 2 }}>
              {["detail", ...(isClosed ? [] : ["edit"])].map(t => (
                <button key={t} onClick={() => setTab(t)} style={{
                  background: "none", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 12,
                  padding: "4px 12px 8px", color: tab === t ? "#0F2A6E" : "#9CA3AF",
                  borderBottom: tab === t ? "2px solid #0F2A6E" : "2px solid transparent",
                  textTransform: "capitalize", letterSpacing: 0.3,
                }}>{t === "detail" ? "Activity" : "Edit"}</button>
              ))}
            </div>
          )}
        </div>

        {error && <div style={{ margin: "12px 20px 0", padding: "8px 12px", background: "#FEE2E2", color: "#B91C1C", borderRadius: 8, fontSize: 12 }}>{error}</div>}

        {/* ── Activity tab ── */}
        {tab === "detail" && !isNew && (
          <div style={{ flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column" }}>
            {loading ? <Spinner /> : (
              <>
                {/* Meta */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
                  <StatusBadge status={form.status} />
                  <PriorityChip priority={form.priority} />
                  <CategoryTag name={catName} color={catColor} />
                  {form.due_date && (
                    <span style={{ fontSize: 11, color: isOverdue(form.due_date, form.status) ? "#DC2626" : "#6B7280", fontWeight: isOverdue(form.due_date, form.status) ? 700 : 400 }}>
                      {isOverdue(form.due_date, form.status) ? "⚠ Overdue · " : "Due "}
                      {fmtDateShort(form.due_date)}
                    </span>
                  )}
                </div>

                {form.body && (
                  <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, marginBottom: 18, whiteSpace: "pre-wrap" }}>{form.body}</div>
                )}

                {/* Quick status change */}
                {!isClosed && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 8, letterSpacing: 0.5 }}>CHANGE STATUS</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {MANUAL_STATUSES.filter(s => s !== form.status).map(s => {
                        const m = STATUS_META[s];
                        return (
                          <button key={s} onClick={() => handleStatusChange(s)}
                            style={{ background: m.bg, color: m.color, border: "none", borderRadius: 16, padding: "4px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                            → {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Divider />

                {/* Timeline */}
                <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", margin: "14px 0 10px", letterSpacing: 0.5 }}>ACTIVITY</div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {timeline.map((item, i) => (
                    <div key={item.id || i} style={{ display: "flex", gap: 12, paddingBottom: 14, position: "relative" }}>
                      {i < timeline.length - 1 && (
                        <div style={{ position: "absolute", left: 10, top: 22, bottom: 0, width: 1, background: "#F3F4F6" }} />
                      )}
                      <div style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0, marginTop: 1,
                        background: item._kind === "comment" ? "#EDE9FE" : item.type === "created" ? "#D1FAE5" : "#E0F2FE",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10,
                      }}>
                        {item._kind === "comment" ? "💬" : item.type === "created" ? "✦" : "↻"}
                      </div>
                      <div style={{ flex: 1, paddingTop: 2 }}>
                        {item._kind === "comment" ? (
                          <div style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px" }}>
                            <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.5, marginBottom: 4 }}>{item.body}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <span style={{ fontSize: 10, color: "#9CA3AF" }}>{fmtDateTime(item.created_at)}</span>
                              {!isClosed && (
                                <button onClick={() => deleteComment(item.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#FCA5A5", padding: 0 }}>Delete</button>
                              )}
                            </div>
                          </div>
                        ) : item.type === "created" ? (
                          <>
                            <span style={{ fontSize: 12, color: "#374151", fontWeight: 600 }}>Ticket created</span>
                            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>{fmtDateTime(item.created_at)}</div>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              {item.auto ? "Auto-closed after 24h:" : "Status changed:"}
                              {" "}<StatusBadge status={item.from_status} /> → <StatusBadge status={item.to_status} />
                            </span>
                            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 4 }}>{fmtDateTime(item.created_at)}</div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {timeline.length === 0 && (
                    <div style={{ fontSize: 12, color: "#D1D5DB", textAlign: "center", padding: "16px 0" }}>No activity yet</div>
                  )}
                </div>

                {/* Comment box */}
                {!isClosed && (
                  <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addComment(); }}
                      placeholder="Add a comment… (Cmd/Ctrl+Enter to submit)"
                      style={{ flex: 1, border: "1px solid #D1D5DB", borderRadius: 8, padding: "8px 10px", fontSize: 13, color: "#0F172A", outline: "none", resize: "vertical", minHeight: 70, fontFamily: "inherit" }} />
                    <button onClick={addComment} style={{ background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer", alignSelf: "flex-end" }}>
                      Post
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Edit / New form ── */}
        {(tab === "edit" || isNew) && !isClosed && (
          <div style={{ flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label style={labelS}>Title *</label>
              <input style={inputS} value={form.title || ""} onChange={e => set("title", e.target.value)} placeholder="What needs to be done?" />
            </div>
            <div>
              <label style={labelS}>Description</label>
              <textarea style={{ ...inputS, minHeight: 90, resize: "vertical" }} value={form.body || ""} onChange={e => set("body", e.target.value)} placeholder="Add context, steps, or acceptance criteria…" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelS}>Status</label>
                <select style={selectS} value={form.status || "New"} disabled={isNew}
                  onChange={e => set("status", e.target.value)}>
                  {(isNew ? ["New"] : MANUAL_STATUSES).map(s => <option key={s}>{s}</option>)}
                </select>
                {isNew && <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 3 }}>Starts as New automatically.</div>}
              </div>
              <div>
                <label style={labelS}>Priority</label>
                <select style={selectS} value={form.priority || "Medium"} onChange={e => set("priority", e.target.value)}>
                  {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={labelS}>Category</label>
                <select style={selectS} value={form.category_id || ""} onChange={e => set("category_id", e.target.value)}>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelS}>Due Date (optional)</label>
                <input type="date" style={inputS} value={form.due_date || ""} min={today()} onChange={e => set("due_date", e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{ padding: "12px 20px 20px", borderTop: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", gap: 8, position: "sticky", bottom: 0, background: "#fff" }}>
          {!isNew && !isClosed ? (
            <button onClick={handleDelete} style={{ background: "#FEE2E2", color: "#B91C1C", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              Delete
            </button>
          ) : <div />}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{ background: "#F3F4F6", color: "#374151", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              {isClosed ? "Close" : "Cancel"}
            </button>
            {!isClosed && (
              <button onClick={handleSave} disabled={saving || !(form.title || "").trim()}
                style={{ background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontWeight: 700, fontSize: 13, cursor: "pointer", opacity: (saving || !(form.title || "").trim()) ? 0.4 : 1 }}>
                {saving ? "Saving…" : isNew ? "Create Ticket" : "Save Changes"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────────────────────────────────────
export default function LifeDesk({ user, signOut }) {
  const navigate = useNavigate();
  const [tickets,      setTickets]      = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [drawer,       setDrawer]       = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [selectedStatuses,   setSelectedStatuses]   = useState(ALL_STATUSES.filter(s => s !== "Closed"));
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [search,             setSearch]             = useState("");
  const [sortField,          setSortField]          = useState("date");       // "date" | "priority"
  const [sortDir,            setSortDir]            = useState("desc");       // "desc" | "asc"

  // Sync user to DB, then load categories and tickets
  const bootstrap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await apiPost("/users/sync", {});
      const [cats, tkts] = await Promise.all([
        apiGet("/categories"),
        apiGet("/tickets"),
      ]);
      setCategories(cats);
      setTickets(tkts);
    } catch (err) {
      setError("Failed to load LifeDesk. Please refresh.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Keep selectedCategories in sync as categories are added/loaded — new ones default to selected
  useEffect(() => {
    setSelectedCategories(prev => {
      const known = new Set(prev);
      const next = categories.map(c => c.id);
      // First load (prev empty) or new categories appeared → include them all
      if (prev.length === 0) return next;
      const newOnes = next.filter(id => !known.has(id));
      return newOnes.length ? [...prev, ...newOnes].filter(id => next.includes(id))
                            : prev.filter(id => next.includes(id));
    });
  }, [categories]);

  const refreshCategories = useCallback(async () => {
    const cats = await apiGet("/categories");
    setCategories(cats);
  }, []);

  const newBlank = () => ({
    title: "", body: "", status: "New", priority: "Medium",
    category_id: categories.find(c => c.locked)?.id || categories[0]?.id || "",
    due_date: "",
  });

  const filtered = useMemo(() => {
    let list = tickets;
    if (selectedStatuses.length   < ALL_STATUSES.length) list = list.filter(t => selectedStatuses.includes(t.status));
    if (categories.length > 0 && selectedCategories.length < categories.length)
      list = list.filter(t => selectedCategories.includes(t.category_id));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => (t.title + " " + (t.body || "")).toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      let cmp;
      if (sortField === "priority") {
        cmp = PRIORITIES.indexOf(b.priority) - PRIORITIES.indexOf(a.priority); // High → Low by default
      } else {
        cmp = new Date(b.created_at) - new Date(a.created_at);                  // Newest → Oldest by default
      }
      return sortDir === "asc" ? -cmp : cmp;
    });
  }, [tickets, selectedStatuses, selectedCategories, categories.length, search, sortField, sortDir]);

  const handleSaved = (updated) => {
    setTickets(ts => {
      const exists = ts.find(t => t.id === updated.id);
      return exists ? ts.map(t => t.id === updated.id ? updated : t) : [updated, ...ts];
    });
  };

  const handleDeleted = (id) => setTickets(ts => ts.filter(t => t.id !== id));

  // Groups filtered tickets by category for column view
  const groupedView = useMemo(() => {
    if (!groupByCategory) return null;
    const knownCatIds = new Set(categories.map(c => c.id));
    const result = [];
    categories.forEach(cat => {
      const catTickets = filtered.filter(t => t.category_id === cat.id);
      if (catTickets.length > 0) result.push({ category: cat, tickets: catTickets });
    });
    // Orphaned tickets (category deleted mid-session)
    const orphaned = filtered.filter(t => !knownCatIds.has(t.category_id));
    if (orphaned.length > 0) {
      result.push({ category: { id: "__orphaned", name: "Uncategorized", color: "#9CA3AF" }, tickets: orphaned });
    }
    return result;
  }, [groupByCategory, filtered, categories]);

  const stats = useMemo(() => {
    const active = tickets.filter(t => t.status !== "Closed");
    return {
      total:     active.length,
      new_:      active.filter(t => t.status === "New").length,
      open:      active.filter(t => t.status === "Open").length,
      onHold:    active.filter(t => t.status === "On Hold").length,
      complete:  active.filter(t => t.status === "Complete").length,
      abandoned: active.filter(t => t.status === "Abandoned").length,
      closed:    tickets.filter(t => t.status === "Closed").length,
    };
  }, [tickets]);

  const selectS = { border: "1px solid #E5E7EB", borderRadius: 7, padding: "6px 10px", fontSize: 12, color: "#374151", background: "#fff", cursor: "pointer", fontFamily: "inherit" };

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", overflowX: "hidden" }}>

      {/* Mobile-responsive nav overrides */}
      <style>{`
        .ld-nav-email  { display: inline; }
        .ld-cat-label  { display: inline; }
        .ld-new-label  { display: inline; }
        .ld-so-label   { display: inline; }
        @media (max-width: 600px) {
          .ld-nav-email { display: none !important; }
          .ld-cat-label { display: none !important; }
          .ld-new-label { display: none !important; }
          .ld-so-label  { display: none !important; }
        }
      `}</style>

      {/* Top Bar */}
      <div style={{
        background: "linear-gradient(180deg, #0F2A6E 0%, #102D75 100%)",
        color: "#fff", padding: "0 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: 58, gap: 8, position: "sticky", top: 0, zIndex: 50,
        boxShadow: "0 2px 12px rgba(15,42,110,.15)",
        borderBottom: "1px solid rgba(96,165,250,.15)",
      }}>
        {/* Logo + wordmark — clickable, returns to landing */}
        <button
          onClick={() => navigate("/")}
          style={{
            display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
            background: "none", border: "none", padding: 0, cursor: "pointer",
            fontFamily: "inherit",
          }}
          title="Back to home"
        >
          <img
            src="/images/logo.png"
            alt="LifeDesk"
            style={{ width: 30, height: 30, borderRadius: 7, objectFit: "contain", display: "block" }}
            onError={(e) => {
              e.target.outerHTML = `
                <div style="
                  width: 30px; height: 30px; border-radius: 7px;
                  border: 1.5px dashed rgba(147,197,253,.5); background: rgba(96,165,250,.1);
                  display: flex; align-items: center; justify-content: center;
                  color: #93C5FD; font-size: 8px; font-weight: 700; letter-spacing: 0.3px;
                ">LOGO</div>
              `;
            }}
          />
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: -0.4, color: "#fff" }}>LifeDesk</span>
        </button>

        {/* Right actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <span className="ld-nav-email" style={{ fontSize: 12, color: "#93C5FD", fontWeight: 500, marginRight: 2 }}>
            {user?.signInDetails?.loginId}
          </span>
          <button onClick={() => setShowSettings(true)} style={{
            background: "rgba(255,255,255,.08)", color: "#CBD5E1",
            border: "1px solid rgba(255,255,255,.1)", borderRadius: 8,
            padding: "7px 10px", fontWeight: 600, fontSize: 13, cursor: "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
          }}>
            ⚙<span className="ld-cat-label"> Categories</span>
          </button>
          <button onClick={() => setDrawer(newBlank())} style={{
            background: "#2563EB", color: "#fff", border: "none", borderRadius: 8,
            padding: "7px 10px", fontWeight: 700, fontSize: 13, cursor: "pointer",
            fontFamily: "inherit", boxShadow: "0 2px 8px rgba(37,99,235,.4)",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            +<span className="ld-new-label"> New</span>
          </button>
          <button onClick={signOut} style={{
            background: "rgba(255,255,255,.08)", color: "#CBD5E1",
            border: "1px solid rgba(255,255,255,.1)", borderRadius: 8,
            padding: "7px 10px", fontWeight: 600, fontSize: 13, cursor: "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
          }}>
            ↪<span className="ld-so-label"> Sign out</span>
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #E5E7EB", padding: "10px 20px", display: "flex", gap: 16, overflowX: "auto" }}>
        {[
          { label: "Active",    value: stats.total,     color: "#0F2A6E" },
          { label: "New",       value: stats.new_,      color: STATUS_META["New"].dot },
          { label: "Open",      value: stats.open,      color: STATUS_META["Open"].dot },
          { label: "On Hold",   value: stats.onHold,    color: STATUS_META["On Hold"].dot },
          { label: "Complete",  value: stats.complete,  color: STATUS_META["Complete"].dot },
          { label: "Abandoned", value: stats.abandoned, color: STATUS_META["Abandoned"].dot },
          { label: "Closed",    value: stats.closed,    color: STATUS_META["Closed"].dot },
        ].map(s => (
          <div key={s.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minWidth: 52 }}>
            <span style={{ fontWeight: 800, fontSize: 20, color: s.color, lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", whiteSpace: "nowrap" }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: "#fff", borderBottom: "1px solid #E5E7EB",
        padding: "12px 20px", display: "flex", gap: 8,
        flexWrap: "wrap", alignItems: "center",
      }}>
        {/* Search */}
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Search tickets…"
          style={{ ...selectS, flex: "1 1 180px", minWidth: 160 }} />

        {/* Status (multi-select) */}
        <MultiSelect
          label="Status"
          options={ALL_STATUSES.map(s => ({ value: s, label: s, color: STATUS_META[s]?.dot }))}
          selected={selectedStatuses}
          onChange={setSelectedStatuses}
        />

        {/* Category (multi-select) */}
        <MultiSelect
          label="Category"
          options={categories.map(c => ({ value: c.id, label: c.name, color: c.color }))}
          selected={selectedCategories}
          onChange={setSelectedCategories}
        />

        {/* Priority sort toggle */}
        <SortToggle
          label="Priority"
          isActive={sortField === "priority"}
          direction={sortDir}
          onClick={() => {
            if (sortField === "priority") setSortDir(d => d === "desc" ? "asc" : "desc");
            else { setSortField("priority"); setSortDir("desc"); }
          }}
        />

        {/* Submit Date sort toggle */}
        <SortToggle
          label="Submit Date"
          isActive={sortField === "date"}
          direction={sortDir}
          onClick={() => {
            if (sortField === "date") setSortDir(d => d === "desc" ? "asc" : "desc");
            else { setSortField("date"); setSortDir("desc"); }
          }}
        />

        {/* Group by category toggle */}
        <button
          onClick={() => setGroupByCategory(g => !g)}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: groupByCategory ? "#EEF2FF" : "#F8FAFC",
            border: `1px solid ${groupByCategory ? "#C7D2FE" : "#E5E7EB"}`,
            borderRadius: 8, padding: "6px 10px", cursor: "pointer",
            fontFamily: "inherit", fontSize: 12, fontWeight: 600,
            color: groupByCategory ? "#3730A3" : "#6B7280",
            transition: "all .15s", whiteSpace: "nowrap",
          }}
        >
          <div style={{
            width: 28, height: 16, borderRadius: 8, flexShrink: 0, position: "relative",
            background: groupByCategory ? "#2563EB" : "#D1D5DB",
            transition: "background .2s",
          }}>
            <div style={{
              position: "absolute", top: 2,
              left: groupByCategory ? 14 : 2,
              width: 12, height: 12, borderRadius: "50%",
              background: "#fff", transition: "left .2s",
              boxShadow: "0 1px 3px rgba(0,0,0,.2)",
            }} />
          </div>
          Group by category
        </button>
      </div>

      {/* ── Grouped view ── */}
      {groupedView && !loading && !error && (
        <div style={{ padding: "16px 20px" }}>
          <style>{`
            .ld-cat-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
              gap: 16px;
              align-items: start;
              justify-content: center;
            }
            @media (min-width: 600px) {
              .ld-cat-grid {
                grid-template-columns: repeat(auto-fit, minmax(260px, 320px));
              }
            }
          `}</style>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 12 }}>
            {filtered.length} ticket{filtered.length !== 1 ? "s" : ""} · {groupedView.length} categor{groupedView.length !== 1 ? "ies" : "y"}
          </div>
          <div className="ld-cat-grid">
            {groupedView.map(({ category, tickets: colTickets }) => (
              <div key={category.id}>
                {/* Column header */}
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  marginBottom: 10, paddingBottom: 8,
                  borderBottom: `2px solid ${category.color}`,
                }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0F172A", flex: 1 }}>
                    {category.name}
                  </span>
                  <span style={{
                    background: category.color + "22", color: category.color,
                    borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700,
                  }}>
                    {colTickets.length}
                  </span>
                </div>
                {/* Cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {colTickets.map(t => {
                    const overdue = isOverdue(t.due_date, t.status);
                    const closed  = t.status === "Closed";
                    return (
                      <div key={t.id} onClick={() => setDrawer(t)} style={{
                        background: closed ? "#FAFAFA" : "#fff",
                        border: "1px solid #E5E7EB", borderRadius: 9,
                        padding: "10px 12px", cursor: "pointer",
                        borderLeft: `3px solid ${closed ? "#D1D5DB" : category.color}`,
                        opacity: closed ? 0.7 : 1,
                        transition: "box-shadow .15s, transform .15s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px rgba(15,42,110,.07)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = ""; e.currentTarget.style.transform = ""; }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
                          <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace", fontWeight: 600 }}>
                            TKT-{String(t.ticket_number).padStart(3, "0")}
                          </span>
                          <StatusBadge status={t.status} />
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: closed ? "#9CA3AF" : "#0F172A", lineHeight: 1.35, marginBottom: 6 }}>
                          {t.title}
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
                          <PriorityChip priority={t.priority} />
                          <span style={{ fontSize: 10, color: "#9CA3AF" }}>{fmtDateTime(t.created_at)}</span>
                        </div>
                        {t.due_date && !closed && (
                          <div style={{ marginTop: 4, fontSize: 10, color: overdue ? "#DC2626" : "#9CA3AF", fontWeight: overdue ? 700 : 400 }}>
                            {overdue ? "⚠ Overdue" : `Due ${fmtDateShort(t.due_date)}`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {groupedView.length === 0 && (
              <div style={{ textAlign: "center", padding: "56px 0", color: "#D1D5DB", gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>No tickets match your filters</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── List view ── */}
      {!groupedView && (
      <div style={{ padding: "16px 20px", maxWidth: 900, margin: "0 auto" }}>
        {loading ? <Spinner /> : error ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#EF4444" }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>⚠</div>
            <div style={{ fontWeight: 600 }}>{error}</div>
            <button onClick={bootstrap} style={{ marginTop: 12, background: "#2563EB", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Retry</button>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 10 }}>
              {filtered.length} ticket{filtered.length !== 1 ? "s" : ""}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {filtered.map(t => {
                const color   = getCatColor(categories, t.category_id);
                const catName = getCatName(categories, t.category_id);
                const overdue = isOverdue(t.due_date, t.status);
                const closed  = t.status === "Closed";
                return (
                  <div key={t.id} onClick={() => setDrawer(t)}
                    style={{
                      background: closed ? "#FAFAFA" : "#fff",
                      border: "1px solid #E5E7EB", borderRadius: 10,
                      padding: "12px 16px", cursor: "pointer",
                      borderLeft: `4px solid ${closed ? "#D1D5DB" : color}`,
                      opacity: closed ? 0.7 : 1,
                      transition: "box-shadow .15s, transform .15s, border-color .15s",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(15,42,110,.08)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      if (!closed) {
                        e.currentTarget.style.borderTopColor = "#CBD5E1";
                        e.currentTarget.style.borderRightColor = "#CBD5E1";
                        e.currentTarget.style.borderBottomColor = "#CBD5E1";
                      }
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.boxShadow = "";
                      e.currentTarget.style.transform = "";
                      e.currentTarget.style.borderTopColor = "#E5E7EB";
                      e.currentTarget.style.borderRightColor = "#E5E7EB";
                      e.currentTarget.style.borderBottomColor = "#E5E7EB";
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace", fontWeight: 600, flexShrink: 0 }}>
                          TKT-{String(t.ticket_number).padStart(3, "0")}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 14, color: closed ? "#9CA3AF" : "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</span>
                      </div>
                      <StatusBadge status={t.status} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
                      <CategoryTag name={catName} color={closed ? "#D1D5DB" : color} />
                      <PriorityChip priority={t.priority} />
                      {t.due_date && !closed && (
                        <span style={{ fontSize: 11, color: overdue ? "#DC2626" : "#9CA3AF", fontWeight: overdue ? 700 : 400 }}>
                          {overdue ? "⚠ Overdue" : `Due ${fmtDateShort(t.due_date)}`}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: "#9CA3AF", marginLeft: "auto" }}>
                        {fmtDateTime(t.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {filtered.length === 0 && !loading && (
                <div style={{ textAlign: "center", padding: "56px 0", color: "#D1D5DB" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📭</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>
                    {tickets.length === 0 ? "No tickets yet. Create your first one!" : "No tickets match your filters"}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      )}

      {drawer && (
        <TicketDrawer
          ticket={drawer}
          categories={categories}
          onClose={() => setDrawer(null)}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}

      {showSettings && (
        <SettingsPanel
          categories={categories}
          onClose={() => setShowSettings(false)}
          onCategoriesChanged={refreshCategories}
        />
      )}
    </div>
  );
}
